import { Directive, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { AsYouType, CountryCode } from 'libphonenumber-js/max';
import { DEFAULT_PHONE_COUNTRY, formatLikeDirective, getPhoneCountry } from '../data/phone-countries.data';
import { E164_MAX_DIGITS, PhoneKind, detectCountryFromInput, internationalDigits, onlyDigits, parseStoredPhone, phoneValidationError, toStoredPhone } from '../data/phone-format';

/** Número nacional BR completo: DDD + 9 dígitos. Mais que isso é 55/prefixo a remover. */
const BR_NATIONAL_MAX_DIGITS = 11;

@Directive({
  selector: '[BrPhoneMask]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneDirective),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneDirective),
      multi: true
    }
  ]
})
export class PhoneDirective implements OnChanges, ControlValueAccessor, Validator {
  /** ISO 3166-1 alpha-2 do país do telefone (ex.: 'BR', 'US', 'PT'). */
  @Input()
  country: CountryCode = DEFAULT_PHONE_COUNTRY;

  /** 'mobile' exige celular; 'any' aceita qualquer tipo de número válido. */
  @Input()
  phoneKind: PhoneKind = 'mobile';

  /** Emitido quando o país muda por causa do valor (writeValue, digitar/colar "+DDI..."). */
  @Output()
  countryChange = new EventEmitter<CountryCode>();

  /** Placeholders for the callbacks which are later provides by the Control Value Accessor*/
  public onChangeCallback = (_: any) => { };
  public onTouchCallback = () => { };
  private _onValidatorChange = () => { };

  /** Dígitos do número nacional exibido no campo. */
  private _national = '';
  /** Último valor armazenado enviado/recebido do model, para não marcar o form como alterado à toa. */
  private _lastStored = '';
  /** Valor recebido via writeValue e ainda não alterado pelo usuário (dado legado não bloqueia a edição). */
  private _writtenValue: string | null = null;
  /** Usuário digitando "+DDI...": o campo mostra o formato internacional até sair do campo. */
  private _plusMode = false;
  /** País que a própria diretiva trocou e ainda vai voltar pelo binding do pai (não reformatar o campo). */
  private _pendingCountry: CountryCode | null = null;

  constructor(private _elementRef: ElementRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['country'] && !changes['country'].firstChange) {
      const isOwnEcho = this._pendingCountry !== null && changes['country'].currentValue === this._pendingCountry;
      this._pendingCountry = null;
      // Eco da troca feita pela própria diretiva: campo e model já estão atualizados
      if (!isOwnEcho) {
        this._plusMode = false;
        this._applyNational(this._cleanValue(this._national), null);
      }
    }
    if (changes['phoneKind'] && !changes['phoneKind'].firstChange) {
      this._onValidatorChange();
    }
  }

  /** Listener for input target of our directive*/
  @HostListener('input')
  onInput(): void {
    this._handleUserText(this._elementRef.nativeElement.value, true, this._caretTokens());
  }

  /** Colar um número internacional ("+351 912 345 678" ou "00351...") não pode ser truncado pelo maxlength do campo. */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const el: HTMLInputElement = this._elementRef.nativeElement;
    if (el.readOnly || el.disabled) {
      return;
    }
    const text = event.clipboardData ? event.clipboardData.getData('text') : '';
    if (internationalDigits(text) !== null) {
      event.preventDefault();
      this._handleUserText(text, false, null);
    }
  }

  @HostListener('blur')
  onBlur(): void {
    if (this._plusMode) {
      this._plusMode = false;
      if (this._national) {
        this._elementRef.nativeElement.value = formatLikeDirective(this.country, this._national);
      }
    } else if (this.country === 'BR' && this._national.startsWith('0')) {
      // prefixo de tronco/operadora digitado: agora o número está completo e pode ser resolvido
      this._applyNational(this._cleanValue(this._national, true), null);
    }
    // Só "+DDI" (país reconhecido) sem número nacional: não deixa texto sem valor no campo.
    // DDI desconhecido é mantido para o validador acusar (phonePattern).
    const el: HTMLInputElement = this._elementRef.nativeElement;
    if (!this._national && el.value.trim().startsWith('+') && (onlyDigits(el.value) === '' || detectCountryFromInput(el.value))) {
      el.value = '';
      this._emitStored('');
    }
    this.onTouchCallback();
  }

  /** From ControlValueAccessor interface*/
  /**
   * Write a new value to the element.
   * O valor do model está no formato armazenado (BR: só dígitos nacionais; demais: +DDI + número).
   */
  public writeValue(modelValue: string): void {
    this._plusMode = false;
    if (modelValue === null || modelValue === undefined || modelValue === '') {
      this._national = '';
      this._lastStored = '';
      this._writtenValue = null;
      this._elementRef.nativeElement.value = '';
      this._setCountry(DEFAULT_PHONE_COUNTRY);
      return;
    }

    this._writtenValue = modelValue.toString();
    const parsed = parseStoredPhone(modelValue);
    this._setCountry(parsed.country);
    this._national = this._cleanValue(parsed.national);
    this._lastStored = toStoredPhone(this.country, this._national);
    this._elementRef.nativeElement.value = formatLikeDirective(this.country, this._national);
  }

  /** From ControlValueAccessor interface*/
  /**
   * Set the function to be called
   * when the control receives a change event.
   */
  public registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  /** From ControlValueAccessor interface*/
  /**
   * Set the function to be called
   * when the control receives a touch event.
   */
  public registerOnTouched(fn: any): void {
    this.onTouchCallback = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this._elementRef.nativeElement.disabled = isDisabled;
  }

  public registerOnValidatorChange(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  /** Return the validation result*/
  validate(c: AbstractControl): ValidationErrors | null {
    const value = c.value ? c.value.toString() : '';
    if (!value) {
      return null;
    }
    // Valor legado carregado e ainda não alterado pelo usuário não bloqueia a edição
    if (this._writtenValue !== null && value === this._writtenValue) {
      return null;
    }
    const { country, national } = parseStoredPhone(value);
    const error = phoneValidationError(country, national, this.phoneKind);
    if (error === 'notMobile') {
      return { phoneNotMobile: true };
    }
    return error ? { phonePattern: true } : null;
  }

  /**
   * Trata o texto digitado/colado. Texto começando com "+" reavalia o país a cada alteração;
   * ao digitar, o campo continua mostrando o "+DDI..." até sair do campo (blur); ao colar, já vai para o formato nacional.
   */
  private _handleUserText(text: string, typing: boolean, caret: number | null): void {
    const intl = internationalDigits(text);
    if (intl !== null) {
      const digits = intl.slice(0, E164_MAX_DIGITS);
      const detected = detectCountryFromInput('+' + digits);
      if (detected) {
        this._setCountry(detected.country);
      }
      if (detected && !typing) {
        this._plusMode = false;
        this._applyNational(this._cleanValue(detected.national), null);
        return;
      }

      this._plusMode = true;
      this._national = detected ? this._cleanValue(detected.national) : '';
      this._setView(new AsYouType().input('+' + digits), caret);
      // DDI desconhecido: mantém como digitado (toStoredPhone) para o validador acusar
      this._emitStored(detected ? toStoredPhone(this.country, this._national) : toStoredPhone(this.country, '+' + digits));
      return;
    }

    this._plusMode = false;
    this._applyNational(this._cleanValue(text, !typing), caret);
  }

  /** Aplica a máscara do país atual no input e atualiza o valor do controle. */
  private _applyNational(nationalDigits: string, caret: number | null): void {
    this._national = nationalDigits;
    // BR com prefixo de tronco/operadora ainda não resolvido: mostra os dígitos crus (a máscara descartaria o prefixo)
    const raw = this.country === 'BR' && nationalDigits.startsWith('0');
    this._setView(!nationalDigits ? '' : raw ? nationalDigits : formatLikeDirective(this.country, nationalDigits), caret);
    this._emitStored(toStoredPhone(this.country, nationalDigits));
  }

  private _emitStored(stored: string): void {
    if (stored !== this._lastStored) {
      this._lastStored = stored;
      this._writtenValue = null;
      this.onChangeCallback(stored);
    }
  }

  /** Quantidade de dígitos (e "+") antes do cursor, para recolocá-lo no mesmo ponto após reformatar. */
  private _caretTokens(): number | null {
    const el: HTMLInputElement = this._elementRef.nativeElement;
    if (typeof el.selectionStart !== 'number' || document.activeElement !== el) {
      return null;
    }
    return (el.value.slice(0, el.selectionStart).match(/[0-9+]/g) || []).length;
  }

  private _setView(view: string, caretTokens: number | null): void {
    const el: HTMLInputElement = this._elementRef.nativeElement;
    el.value = view;
    if (caretTokens === null || document.activeElement !== el) {
      return;
    }
    let position = 0;
    let tokens = 0;
    while (position < view.length && tokens < caretTokens) {
      if (/[0-9+]/.test(view[position])) {
        tokens++;
      }
      position++;
    }
    el.setSelectionRange(position, position);
  }

  /**
   * Atualiza o país na hora e avisa o componente pai. A emissão é adiada para fora do ciclo de
   * detecção de mudanças atual (writeValue roda durante a renderização do pai).
   */
  private _setCountry(country: CountryCode): void {
    if (country === this.country) {
      return;
    }
    this.country = country;
    this._pendingCountry = country;
    Promise.resolve().then(() => this.countryChange.emit(country));
  }

  /**
   * Mantém só dígitos e respeita o limite do país (DDI + nacional ≤ 15 dígitos). No Brasil remove antes o 55 e o
   * prefixo de tronco/operadora ("0 21"), para o limite não truncar dígitos que serão descartados.
   */
  private _cleanValue(viewValue: string, resolvePrefix = false): string {
    let digits = (viewValue || '').replace(/[^0-9]/g, '');
    const cap = getPhoneCountry(this.country).digitCap;
    if (this.country === 'BR') {
      if (digits.startsWith('0') && !resolvePrefix) {
        // Prefixo de tronco/operadora sendo digitado ("0 21 85..."): enquanto parcial é ambíguo (0+21 parece DDD),
        // então mantém os dígitos crus e só resolve ao sair do campo ou colar
        return digits.slice(0, E164_MAX_DIGITS);
      }
      if (digits.length > BR_NATIONAL_MAX_DIGITS || digits.startsWith('0')) {
        digits = toStoredPhone('BR', digits);
      }
    }
    return digits.slice(0, cap);
  }
}
